import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ApplicationSuccess.css';

const ApplicationSuccess = ({ candidate, job, onClose, onApplyAgain }) => {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  // Fall back to router navigation when rendered as a route (no props passed) —
  // previously onApplyAgain/onClose were undefined so the buttons did nothing.
  const handleApplyAgain = () => {
    if (onApplyAgain) onApplyAgain();
    else navigate('/careers');
  };

  const handleClose = () => {
    if (onClose) onClose();
    else navigate('/');
  };

  return (
    <div className="success-overlay">
      <div className="success-modal">
        {/* Animated Checkmark */}
        <div className="success-animation">
          <div className="checkmark-circle">
            <div className="checkmark-stem"></div>
            <div className="checkmark-kick"></div>
          </div>
          <div className="confetti">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="confetti-piece"></div>
            ))}
          </div>
        </div>

        {/* Success Content */}
        <div className="success-content">
          <h1>Application Submitted!</h1>
          <p className="success-message">
            Thank you for applying to <strong>{job?.jobTitle}</strong> at our company. 
            We've received your application and will review it carefully.
          </p>

          {/* Application Details */}
          <div className="application-details">
            <div className="detail-card">
              <div className="detail-header">
                <h3>Application Details</h3>
                <span className="application-id">Ref: {candidate?._id?.slice(-8) || 'N/A'}</span>
              </div>
              
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Position Applied</span>
                  <span className="detail-value">{job?.jobTitle}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Department</span>
                  <span className="detail-value">{job?.department}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Applicant Name</span>
                  <span className="detail-value">{candidate?.name}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{candidate?.email}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Application Date</span>
                  <span className="detail-value">{new Date().toLocaleDateString()}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className="status-badge status-received">Application Received</span>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="next-steps">
            <h3>What Happens Next?</h3>
            <div className="steps-timeline">
              <div className="step">
                <div className="step-icon">📨</div>
                <div className="step-content">
                  <h4>Application Review</h4>
                  <p>Our recruitment team will review your application and resume within 3-5 business days.</p>
                </div>
              </div>
              
              <div className="step">
                <div className="step-icon">🤖</div>
                <div className="step-content">
                  <h4>AI Screening</h4>
                  <p>Your application will go through our AI screening process to match your skills with the job requirements.</p>
                </div>
              </div>
              
              <div className="step">
                <div className="step-icon">📞</div>
                <div className="step-content">
                  <h4>Interview Invitation</h4>
                  <p>If selected, you'll receive an email invitation for the next stage of the process.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="contact-info">
            <div className="contact-card">
              <h3>Need Help?</h3>
              <p>If you have any questions about your application, please contact our HR team:</p>
              <div className="contact-details">
                <div className="contact-item">
                  <span className="contact-icon">📧</span>
                  <span>hr@company.com</span>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">📱</span>
                  <span>+1 (555) 123-4567</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="success-actions">
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/careers')}
            >
              <span className="btn-icon">←</span>
              Back to Jobs
            </button>

            <button
              className="btn btn-secondary"
              onClick={handleApplyAgain}
            >
              <span className="btn-icon">➕</span>
              Apply for Another Position
            </button>

            <button
              className="btn btn-primary"
              onClick={handleClose}
            >
              <span className="btn-icon">🏠</span>
              Return to Home
            </button>
          </div>

          {/* Download Receipt */}
          <div className="download-section">
            <button 
              className="btn-download"
              onClick={() => window.print()}
            >
              <span className="download-icon">📄</span>
              Download Application Receipt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationSuccess;